import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import { reportError } from "@template/shared";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ((error: Error, reset: () => void) => ReactNode) | undefined;
  onError?: ((error: Error, info: ErrorInfo) => void) | undefined;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, { route: window.location.pathname });
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return <DefaultFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <Card className="mx-auto max-w-xl" data-testid="error-boundary-card">
      <CardHeader>
        <CardTitle>Something went wrong</CardTitle>
        <CardDescription>An unexpected error occurred while rendering this page.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive"
          data-testid="error-boundary-message"
          role="alert"
        >
          {error.message}
        </div>
        <div className="flex gap-2">
          <Button onClick={reset} data-testid="error-boundary-retry-button">
            Try again
          </Button>
          <Button asChild variant="outline">
            <Link to="/dashboard" data-testid="error-boundary-home-link">
              Return home
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
